import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import SEOHead from "@/components/seo/SEOHead";
import { Star } from "lucide-react";

interface FeedbackQuestion {
  question_key: string;
  question_text: string;
  question_type: "rating" | "text" | "yes_no" | "scale";
  sort_order: number;
}

const Feedback = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [providerName, setProviderName] = useState("");
  const [questions, setQuestions] = useState<FeedbackQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, { rating?: number; text_value?: string }>>({});

  useEffect(() => {
    if (!token) return;
    loadForm();
  }, [token]);

  const loadForm = async () => {
    try {
      const { data, error: err } = await supabase.functions.invoke("send-feedback-email", {
        body: { action: "get-form", token },
      });

      if (err) throw err;
      if (data?.error) {
        if (data.completed) {
          setSubmitted(true);
        } else {
          setError(data.error);
        }
        setLoading(false);
        return;
      }

      setProviderName(data.provider_name || "");
      setQuestions(data.questions || []);
    } catch (e: any) {
      setError(t("feedback.invalidLink"));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const responses = questions.map((q) => ({
        question_key: q.question_key,
        rating: answers[q.question_key]?.rating ?? null,
        text_value: answers[q.question_key]?.text_value ?? null,
      }));

      const { data, error: err } = await supabase.functions.invoke("send-feedback-email", {
        body: { action: "submit", token, responses },
      });

      if (err || data?.error) throw new Error(data?.error || t("common.error"));
      setSubmitted(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const setRating = (key: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [key]: { ...prev[key], rating: value } }));
  };

  const setText = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: { ...prev[key], text_value: value } }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("common.loading")}</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEOHead title={t("feedback.seoThankYou")} noindex canonical={`/feedback/${token}`} />
        <div className="max-w-md mx-auto text-center p-12">
          <div className="w-16 h-16 mx-auto mb-6 bg-accent/10 rounded-full flex items-center justify-center">
            <Star className="w-8 h-8 text-accent" />
          </div>
          <h1 className="text-headline mb-4">{t("feedback.thankYouTitle")}</h1>
          <p className="text-caption">{t("feedback.thankYouDesc")}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <SEOHead title="Feedback" noindex canonical={`/feedback/${token}`} />
        <div className="max-w-md mx-auto text-center p-12">
          <h1 className="text-headline mb-4">{t("feedback.errorTitle")}</h1>
          <p className="text-caption">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={t("feedback.formTitle")} noindex canonical={`/feedback/${token}`} />

      <div className="max-w-lg mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <p className="text-micro mb-2">CRAFTFINDUS</p>
          <h1 className="text-headline mb-3">{t("feedback.formTitle")}</h1>
          {providerName && (
            <p className="text-caption" dangerouslySetInnerHTML={{ __html: t("feedback.formDesc", { provider: providerName }) }} />
          )}
        </div>

        {/* Questions */}
        <div className="space-y-8">
          {questions.map((q) => (
            <div key={q.question_key} className="p-6 border border-border bg-card">
              <p className="text-body font-medium mb-4">{q.question_text}</p>

              {q.question_type === "rating" && (
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(q.question_key, star)}
                      className="p-1 transition-colors"
                    >
                      <Star
                        className={`w-8 h-8 ${
                          (answers[q.question_key]?.rating || 0) >= star
                            ? "text-accent fill-accent"
                            : "text-muted-foreground/30"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              )}

              {q.question_type === "yes_no" && (
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={answers[q.question_key]?.text_value === "ja" ? "commit" : "quiet"}
                    size="sm"
                    onClick={() => setText(q.question_key, "ja")}
                  >
                    {t("common.yes")}
                  </Button>
                  <Button
                    type="button"
                    variant={answers[q.question_key]?.text_value === "nein" ? "commit" : "quiet"}
                    size="sm"
                    onClick={() => setText(q.question_key, "nein")}
                  >
                    {t("common.no")}
                  </Button>
                </div>
              )}

              {q.question_type === "text" && (
                <Textarea
                  placeholder={t("feedback.answerPlaceholder")}
                  value={answers[q.question_key]?.text_value || ""}
                  onChange={(e) => setText(q.question_key, e.target.value)}
                  rows={3}
                />
              )}

              {q.question_type === "scale" && (
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(q.question_key, n)}
                      className={`w-9 h-9 text-sm border transition-colors ${
                        (answers[q.question_key]?.rating || 0) === n
                          ? "bg-accent text-accent-foreground border-accent"
                          : "border-border text-muted-foreground hover:border-accent/50"
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Submit */}
        <div className="mt-10">
          <Button
            variant="commit"
            className="w-full"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? t("feedback.submitting") : t("feedback.submitBtn")}
          </Button>
          <p className="text-micro text-center mt-4 text-muted-foreground">
            {t("feedback.anonymous")}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Feedback;
